import React, {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef
} from 'react';
import PropTypes from 'prop-types';
import {
  useStravaAnalytics,
  useStravaErrorLogging,
  useStravaTranslations
} from '@strava/container-context';
import { useWindowScrollTop } from '@strava/react-hooks/src/use-window-scroll-top';
import { EXPERIMENT_COHORTS } from '@strava/constants/src/appConstants';
import clsx from '@strava/ui/clsx';
import Header from './components/Header';
import Footer from './components/Footer';
import { FrontDoorsPageContainerContextProvider } from './state/FrontDoorsPageContainerContext';
import { reducer } from './state/FrontDoorsPageContainer.reducer';
import { UPDATE_WINDOW_SCROLL_TOP } from './state/FrontDoorsPageContainer.actions';
import styles from './FrontDoorsPageContainer.module.scss';

/**
 *
 * @param {string} cohort - Defaults to control. Front-door pages can only have one experiment
 *      running on them at a time because we depend on snowplow cookies for cohorting. This
 *      prop is built-in to be reusable for any pages that need to show different treatments based
 *      on the cohort in which an athlete is placed.
 *
 * @param {object} authorizationUrls - (optional) urls to be used for facebook, google, apple and email sign-up
 *      options
 *
 * @param {boolean} showSmartBanner - Controls whether the smartbanner should be shown on the mobile-web version of the page.
 *                                    Default is false (hide).
 *
 * @param {string} userAgent - Request user agent. This is required for SSR (nextJS apps).
 *
 * @param {string} cpraI18nPrefix - (optional) path to 'cpra_compliance_cta' translations, if different from just `legal` (as is the
 *       case for when the FrontDoorsPageContainer is used in active instead of a nextjs app)
 *
 * @param {boolean} menuConfig.showLoginOptionOnly - (optional) true if we want to hide the `register` options, such as on the `/register` page.
 *       Will also adjust styling for the login option so it gets primary/highlighted styling. Defaults to false.
 *
 * @param {function} onScreenEnter - (optional) Callback function, called on screen enter
 *
 * @param {function} onDesktopHeaderLogoClick - (optional) Callback function, called when athlete clicks on Strava logo on desktop header
 *
 * @param {function} onDesktopHeaderLoginBtnClick - (optional) Callback function, called when athlete clicks on 'Login' on desktop header
 *
 * @param {function} onDesktopHeaderSignupBtnClick - (optional) Callback function, called when athlete clicks on 'Join for Free' on desktop header
 *
 * @param {function} onMobileHeaderLogoClick - (optional) Callback function, called when athlete clicks on Strava logo on mobile header
 *
 * @param {function} onMobileHeaderSignupBtnClick - (optional) Callback function, called when athlete clicks on 'Get the App' on mobile header
 *
 * @param {function} onMobileHeaderMenuOpen - (optional) Callback function, called when athlete opens the menu on mobile header
 *
 * @param {function} onMobileHeaderMenuClose - (optional) Callback function, called when athlete closes the menu on mobile header
 *
 * @param {function} onMobileHeaderMenuLoginBtnClick - (optional) Callback function, called when athlete clicks on 'Login' in the menu on mobile header
 *
 * @param {function} onMobileHeaderMenuSignupBtnClick - (optional) Callback function, called when athlete clicks on 'Get the App' in the menu on mobile header
 *
 * @param {function} onFooterAppStoreBadgeClick - (optional) Callback function, called when athlete clicks on App store badges on footer
 *
 */
const FrontDoorsPageContainer = ({
  children,
  locale,
  page,
  category,
  showSmartBanner,
  dataSharingOptedOut,
  cohort,
  cpraI18nPrefix,
  userAgent,
  className,
  header,
  footer,
  menuConfig,
  onScreenEnter,
  onDesktopHeaderLogoClick,
  onDesktopHeaderLoginBtnClick,
  onDesktopHeaderSignupBtnClick,
  onMobileHeaderLogoClick,
  onMobileHeaderSignupBtnClick,
  onMobileHeaderMenuOpen,
  onMobileHeaderMenuClose,
  onMobileHeaderMenuLoginBtnClick,
  onMobileHeaderMenuSignupBtnClick,
  onFooterAppStoreBadgeClick
}) => {
  const { track: _track } = useStravaAnalytics();
  const { t: _t } = useStravaTranslations();
  const noop = () => {};

  const { showHeader } = header;
  const { showFooter } = footer;

  const {
    logError,
    setTags = noop,
    setContext = noop
  } = useStravaErrorLogging();

  const [windowScrollTop] = useWindowScrollTop();

  // override translations helper function
  const t = useMemo(
    () => (key, params) =>
      _t(`front_doors_page_container_ui.${key}`, { ...params }),
    [_t]
  );

  const headerRef = useRef();

  // override analytics helper function
  const track = useCallback(
    (fields = {}) => {
      _track({
        category,
        page,
        ...fields,
        properties: {
          ...fields.properties
        }
      });
    },
    [_track, category, page]
  );

  const [state, dispatch] = useReducer(reducer, {
    category,
    cohort,
    headerRef,
    locale,
    page,
    userAgent,
    windowScrollTop,
    dataSharingOptedOut,
    cpraI18nPrefix,
    header,
    footer,
    menuConfig,
    showSmartBanner
  });

  const providerState = useMemo(
    () => ({
      state,
      dispatch,
      track,
      t,
      logError,
      onScreenEnter,
      onDesktopHeaderLogoClick,
      onDesktopHeaderLoginBtnClick,
      onDesktopHeaderSignupBtnClick,
      onMobileHeaderLogoClick,
      onMobileHeaderSignupBtnClick,
      onMobileHeaderMenuOpen,
      onMobileHeaderMenuClose,
      onMobileHeaderMenuLoginBtnClick,
      onMobileHeaderMenuSignupBtnClick,
      onFooterAppStoreBadgeClick
    }),
    [
      logError,
      state,
      t,
      track,
      onScreenEnter,
      onDesktopHeaderLogoClick,
      onDesktopHeaderLoginBtnClick,
      onDesktopHeaderSignupBtnClick,
      onMobileHeaderLogoClick,
      onMobileHeaderSignupBtnClick,
      onMobileHeaderMenuOpen,
      onMobileHeaderMenuClose,
      onMobileHeaderMenuLoginBtnClick,
      onMobileHeaderMenuSignupBtnClick,
      onFooterAppStoreBadgeClick
    ]
  );

  // screen enter
  // - track analytics
  // - set container-level tags and context for sentry error tracking and performance monitoring
  useEffect(() => {
    setTags({
      'frontDoorsPageContainer.locale': locale,
      'frontDoorsPageContainer.cohort': cohort,
      'frontDoorsPageContainer.page': page
    });
    setContext('frontDoorsPageContainer', {
      showSmartBanner,
      category
    });
  }, [category, cohort, locale, showSmartBanner, page, setContext, setTags]);

  useEffect(() => {
    onScreenEnter();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Note: Empty [] required to ensure that `onScreenEnter` only runs once

  useEffect(() => {
    dispatch({ type: UPDATE_WINDOW_SCROLL_TOP, payload: { windowScrollTop } });
  }, [windowScrollTop]);

  return (
    <FrontDoorsPageContainerContextProvider value={providerState}>
      <div className={clsx(styles.body, className)} data-cy="fdp-container">
        {showHeader && <Header ref={headerRef} />}
        {children}
        {showFooter && <Footer />}
      </div>
    </FrontDoorsPageContainerContextProvider>
  );
};

FrontDoorsPageContainer.defaultProps = {
  locale: null,
  showSmartBanner: false,
  cohort: EXPERIMENT_COHORTS.control,
  userAgent:
    typeof window === 'undefined' ? null : window?.navigator?.userAgent,
  className: null,
  cpraI18nPrefix: 'legal',
  menuConfig: {
    showLoginOptionOnly: false
  },
  header: {
    showHeader: true
  },
  footer: {
    showFooter: true
  },
  onScreenEnter: () => {},
  onDesktopHeaderLogoClick: () => {},
  onDesktopHeaderLoginBtnClick: () => {},
  onDesktopHeaderSignupBtnClick: () => {},
  onMobileHeaderLogoClick: () => {},
  onMobileHeaderSignupBtnClick: () => {},
  onMobileHeaderMenuOpen: () => {},
  onMobileHeaderMenuClose: () => {},
  onMobileHeaderMenuLoginBtnClick: () => {},
  onMobileHeaderMenuSignupBtnClick: () => {},
  onFooterAppStoreBadgeClick: () => {}
};

FrontDoorsPageContainer.propTypes = {
  children: PropTypes.node.isRequired,
  locale: PropTypes.string,
  page: PropTypes.string.isRequired,
  category: PropTypes.string.isRequired,
  showSmartBanner: PropTypes.bool,
  dataSharingOptedOut: PropTypes.bool.isRequired,
  cpraI18nPrefix: PropTypes.string,
  cohort: PropTypes.oneOf(Object.values(EXPERIMENT_COHORTS)),
  userAgent: PropTypes.string,
  className: PropTypes.string,
  menuConfig: PropTypes.shape({
    showLoginOptionOnly: PropTypes.bool
  }),
  header: PropTypes.shape({
    showHeader: PropTypes.bool
  }),
  footer: PropTypes.shape({
    showFooter: PropTypes.bool
  }),
  onScreenEnter: PropTypes.func,
  onDesktopHeaderLogoClick: PropTypes.func,
  onDesktopHeaderLoginBtnClick: PropTypes.func,
  onDesktopHeaderSignupBtnClick: PropTypes.func,
  onMobileHeaderLogoClick: PropTypes.func,
  onMobileHeaderSignupBtnClick: PropTypes.func,
  onMobileHeaderMenuOpen: PropTypes.func,
  onMobileHeaderMenuClose: PropTypes.func,
  onMobileHeaderMenuLoginBtnClick: PropTypes.func,
  onMobileHeaderMenuSignupBtnClick: PropTypes.func,
  onFooterAppStoreBadgeClick: PropTypes.func
};

export default FrontDoorsPageContainer;
